import { Trans } from 'react-i18next';
import { useMemo, useState } from 'react';
import { Alert } from 'componets/ui';
import { BearLossCalculatorModal } from 'pages/Account/Overview/BearLossCalculator/BearLossCalculatorModal';
import {useAppDispatch, useAppSelector, useTranslate} from 'hooks';
import { ClientRole, Paths, RequestStatus, VerificationStatus } from 'utils/constants';
import Utils from 'utils/utils';
import { selectUser } from 'selectors';
import styles from '../index.module.scss';
import moment from 'moment';
import {SettingsPasswordModal} from "../../../../pages/Account/Settings/SettingsPasswordModal";
import {getUserProfile} from "../../../../features/user/actions";
import {diffNow} from "../../../../utils/DateTime";
import {isLt, isRo} from "../../../../utils";

const WORDS = [
	'identifyIsNotVerifiedText',
	'verifyNowText',
	'taxResidencyNotSpecifiedText',
	'specifyNowText',
	'quizStatusText',
	'startText',
	'alertRiskToleranceText',
	'verifyText',
	'alertIdentityExpiredText',
	'alertCompanyExpiredText',
	'alertCompanyExpiredSoonText',
	'alertIdentityExpiredSoonText',
	'readMoreText',
	'alertDASFormText',
	'alertIdentityNullText',
	'alertIdentityNullCompanyText',
	'alertRiskToleranceText2',
	'toUpdateQuizText',
	'passwordExpiredText',
	'changePasswordText',
	'identifyInProgressText',
	'checkIdentityText'
];

const LT = 'LT';

export const AccountLayoutContentAlerts = () => {
	const user = useAppSelector(selectUser);
	const dispatch = useAppDispatch();

	const { documents, status } = useAppSelector((state) => state.overview);

	const t = useTranslate(WORDS);

	const [isOpenRiskTolerance, setIsOpenRiskTolerance] = useState(false);

	const { verificationStatus } = user;

	const [isOpenChangePassword, setIsOpenChangePassword] = useState(false);

	const isIdentityActive = useMemo(
		() => Utils.isDocumentActive(documents.identity),
		[documents.identity],
	);

	const isIdentityExpiredSoon = useMemo(
		() => Utils.isDocumentExpiredSoon(documents.identity),
		[documents.identity],
	);
	const isIdentityNull = useMemo(
		() => Utils.isDocumentNull(documents.identity),
		[documents.identity],
	);

	const isIdentityCompanyActive = useMemo(
		() => Utils.isDocumentActive(documents.company),
		[documents.company],
	);

	const isIdentityCompanyExpiredSoon = useMemo(
		() => Utils.isDocumentExpiredSoon(documents.company),
		[documents.company],
	);

	const isIdentityCompanyNull = useMemo(
		() => Utils.isDocumentNull(documents.company),
		[documents.company],
	);

	const isRiskToleranceExpired = useMemo(() => {
		return moment().diff(moment(user.riskTolerance?.date), 'months') >= 23;
	}, [user.riskTolerance]);

	const isFailedVeriff = verificationStatus === VerificationStatus.FAILED;
	const isInProgressVeriff = verificationStatus === VerificationStatus.IN_PROGRESS;

	const isDASAlertDecember =
		user.userType === ClientRole.USER &&
		user.taxResidence?.country.iso !== LT &&
		documents.das1?.length > 0 &&
		Utils.isDateInDecember(new Date());

	const isDASAlert =
		user.taxResidence &&
		user.userType === ClientRole.USER &&
		user.taxResidence?.country.iso !== LT &&
		!(documents.das1?.find(({ expired }) => expired === 0)) &&
		user.taxResidence?.doubleTaxationAvoidance;

	const isCompany = user.userType === ClientRole.COMPANY;

	const isExpiredIdentity = documents.identity && !isIdentityActive && !isIdentityNull;

	const isLoading = status === RequestStatus.PENDING;

	if (isLoading) return null;

	const isExistsKYC = user.quizStatuses?.KYC.status !== null;
	const isExistsAML = user.quizStatuses?.AML.status !== null;
	const isExpiredKYC = user.quizStatuses.KYC?.date && diffNow(user.quizStatuses.KYC.date, 'months') >= 23;
	const isExpiredAML = user.quizStatuses.AML?.date && diffNow(user.quizStatuses.AML.date, 'months') >= 23;
	return (
		<>
			{verificationStatus !== VerificationStatus.VERIFIED && !isExpiredIdentity && (
				<Alert
					text={
						isFailedVeriff ? (
							<Trans
								i18nKey="messageVerificationFailedText"
								components={{
									l: (
										<a
											href="mailto:info@crowdpear.com"
											target="_blank"
											className={styles.link}
											rel="noreferrer"
										/>
									),
								}}
							/>
						) : (
							isInProgressVeriff ? t.identifyInProgressText : t.identifyIsNotVerifiedText
						)
					}
					path={isFailedVeriff ? '' : Paths.VERIFY_IDENTITY}
					linkText={isInProgressVeriff ?  t.checkIdentityText : t.verifyNowText}
				/>
			)}

			{!!user?.passwordExpired && (
				<Alert
					text={t.passwordExpiredText}
					isButton={true}
					onClick={() => setIsOpenChangePassword(true)}
					linkText={t.changePasswordText}
				/>
			)}

			{!user.taxResidence && (
				<Alert
					text={t.taxResidencyNotSpecifiedText}
					path={Paths.RESIDENCY}
					linkText={t.specifyNowText}
				/>
			)}

			{(isExistsAML && isExistsKYC) && (
				<>
					{(isExpiredKYC || isExpiredAML)  && (
						<Alert
							text={t.toUpdateQuizText}
							path={Paths.KYC}
							linkText={t.startText}
						/>
					)}
				</>
			)}

			{(!isExistsAML || !isExistsKYC) && (
				<Alert
					text={t.quizStatusText}
					path={(user.quizStatuses?.KYC.status && !isExpiredKYC) ? Paths.AML : Paths.KYC}
					linkText={t.startText}
				/>
			)}

			{!user.riskTolerance?.value && (
				<Alert
					text={t.alertRiskToleranceText}
					path=""
					linkText={t.startText}
					isButton
					onClick={() => setIsOpenRiskTolerance(true)}
				/>
			)}

			{user.riskTolerance?.value && isRiskToleranceExpired && (
				<Alert
					text={t.alertRiskToleranceText2}
					path=""
					linkText={t.startText}
					isButton
					onClick={() => setIsOpenRiskTolerance(true)}
				/>
			)}

			<BearLossCalculatorModal
				isOpen={isOpenRiskTolerance}
				onClose={() => setIsOpenRiskTolerance(false)}
			/>

			{isExpiredIdentity && (
				<Alert
					text={t.alertIdentityExpiredText}
					linkText={t.verifyText}
					path={Paths.VERIFY_IDENTITY}
				/>
			)}

			{documents.identity && isIdentityExpiredSoon && isIdentityActive && (
				<Alert text={t.alertIdentityExpiredSoonText} />
			)}

			{documents.company && !isIdentityCompanyActive && !isIdentityCompanyNull && (
				<Alert
					linkText={t.readMoreText}
					path={
						isRo && 'https://crowdpear.com/ro/help/how-to-verify-the-company-account/ ' ||
						isLt && 'https://crowdpear.com/lt/duk/kaip-patvirtinti-imones-paskyra/' ||
						'https://crowdpear.com/help/how-to-verify-the-company-account/ '
					}
					text={t.alertCompanyExpiredText}
				/>
			)}

			{documents.company && isIdentityCompanyExpiredSoon && isIdentityCompanyActive && (
				<Alert
					linkText={t.readMoreText}
					path={
						isRo && 'https://crowdpear.com/ro/help/how-to-verify-the-company-account/ ' ||
						isLt && 'https://crowdpear.com/lt/duk/kaip-patvirtinti-imones-paskyra/' ||
						'https://crowdpear.com/help/how-to-verify-the-company-account/ '
					}
					text={t.alertCompanyExpiredSoonText}
				/>
			)}

			{isCompany && !documents.company && (
				<Alert
					linkText={t.readMoreText}
					path={
						isRo && 'https://crowdpear.com/ro/help/how-to-verify-the-company-account/ ' ||
						isLt && 'https://crowdpear.com/lt/duk/kaip-patvirtinti-imones-paskyra/' ||
						'https://crowdpear.com/help/how-to-verify-the-company-account/ '
					}
					text={
						<Trans
							i18nKey="alertCompanyVerificationText"
							components={{
								l: (
									<a
										href="mailto:info@crowdpear.com"
										target="_blank"
										className={styles.link}
										rel="noreferrer"
									/>
								),
							}}
						/>
					}
				/>
			)}

			{isIdentityNull && documents.identity && <Alert text={t.alertIdentityNullText} />}

			{isIdentityCompanyNull && documents.company && (
				<Alert text={t.alertIdentityNullCompanyText} />
			)}

			{isDASAlertDecember && (
				<Alert
					target="_blank"
					linkText={t.readMoreText}
					path="https://crowdpear.com/help/what-documents-do-i-need-to-submit-to-reduce-the-taxes-paid-in-my-own-country/"
					text={
						<Trans
							i18nKey="alertDASFormDecemberText"
							values={{ year: new Date().getFullYear() + 1 }}
						/>
					}
				/>
			)}

			{isDASAlert && (
				<Alert
					target="_blank"
					linkText={t.readMoreText}
					path={
						isRo && 'https://crowdpear.com/lt/duk-category/mokesciai' ||
						isLt && 'https://crowdpear.com/lt/duk-category/mokesciai' ||
						'https://crowdpear.com/help-category/taxes'
					}
					text={t.alertDASFormText}
				/>
			)}

			{isOpenChangePassword && (
				<SettingsPasswordModal
					isOpen={isOpenChangePassword}
					onClose={() => {
						setIsOpenChangePassword(false);
						dispatch(getUserProfile());
					}}
				/>
			)}
		</>
	);
};
